<template>
  <v-menu offset-y location="bottom">
    <template #activator="{ props: activator }">
      <v-btn
        v-bind="activator"
        class="business-switcher-btn"
        :class="{ 'business-switcher-btn--icon-only': iconOnly }"
        variant="outlined"
        color="primary"
        density="comfortable"
        append-icon="mdi-chevron-down"
        :aria-label="iconOnly ? `Switch business (current: ${currentLabel})` : undefined"
      >
        <v-icon start>mdi-store</v-icon>
        <span v-if="!iconOnly" class="business-switcher-current">{{ currentLabel }}</span>
      </v-btn>
    </template>
    <v-list density="comfortable" class="business-switcher-menu">
      <v-list-subheader v-if="iconOnly" class="business-switcher-current-header">
        {{ currentLabel }}
      </v-list-subheader>
      <v-divider v-if="iconOnly" />
      <v-list-item
        v-for="id in businessIds"
        :key="id"
        :active="id === selectedId"
        @click="onSelect(id)"
      >
        <v-list-item-title>{{ labelFor(id) }}</v-list-item-title>
        <v-list-item-subtitle class="business-switcher-id">{{ id }}</v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { computed, watch } from "vue";
import { useDisplay } from "vuetify";
import { getBusinessDisplayName } from "@/firebase-business.js";
import {
  businessContext,
  setDisplayName,
} from "@/stores/businessContext.js";

export default {
  name: "BusinessSwitcher",
  props: {
    businessIds: { type: Array, required: true },
    selectedId: { type: String, default: "" },
  },
  emits: ["change"],
  setup(props, { emit }) {
    const display = useDisplay();
    // Collapse to icon-only on Vuetify's smAndDown (<=599px). The full
    // business name moves into the popped menu header so users can still
    // confirm which business they're in.
    const iconOnly = computed(() => display.smAndDown.value);

    // We read names from the shared `businessContext.displayNames` cache so
    // a save on the branding page surfaces here without a refetch. We still
    // do a lazy fetch the first time we see a business id we don't know.
    const refreshNames = async (ids) => {
      for (const id of ids) {
        if (businessContext.displayNames[id] != null) continue;
        try {
          const name = await getBusinessDisplayName(id);
          setDisplayName(id, name || id);
        } catch {
          setDisplayName(id, id);
        }
      }
    };

    watch(
      () => props.businessIds,
      (ids) => {
        if (Array.isArray(ids) && ids.length) refreshNames(ids);
      },
      { immediate: true }
    );

    const labelFor = (id) => businessContext.displayNames[id] || id;
    const currentLabel = computed(() => labelFor(props.selectedId));

    const onSelect = (id) => {
      if (id && id !== props.selectedId) emit("change", id);
    };

    return { labelFor, currentLabel, onSelect, iconOnly };
  },
};
</script>

<style scoped>
.business-switcher-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0 !important;
  max-width: 260px;
  border-radius: 999px !important;
  border-color: rgba(0, 109, 62, 0.4) !important;
  color: var(--lulu-primary) !important;
  background: transparent !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.business-switcher-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.25) !important;
  transform: translateY(-1px);
  border-color: var(--lulu-primary) !important;
}

.business-switcher-btn--icon-only {
  min-width: 0;
  padding: 0 8px;
}

.business-switcher-current {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 200px;
  vertical-align: middle;
}

.business-switcher-menu {
  min-width: 240px;
  max-width: 320px;
}

.business-switcher-current-header {
  font-weight: 700;
  color: var(--lulu-text-strong);
  font-size: 13px;
}

.business-switcher-id {
  font-size: 11px;
  color: var(--lulu-text-subtle);
}
</style>
